import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string[];
  image?: string;
  type?: 'website' | 'article';
  author?: string;
  publishedTime?: string;
  modifiedTime?: string;
  article?: {
    author: string;
    publishedTime: string;
    modifiedTime?: string;
    section: string;
    tags: string[];
  };
}

const SEO = ({
  title,
  description,
  keywords = [],
  image = '/logo.png',
  type = 'website',
  author,
  publishedTime,
  modifiedTime,
  article
}: SEOProps) => {
  const location = useLocation();
  const baseUrl = 'https://futurestation.com.br';
  const currentUrl = `${baseUrl}${location.pathname}`;
  
  // Title padrão e otimizado
  const defaultTitle = 'Future Station - Especialistas em Fluig e TOTVS';
  const fullTitle = title ? `${title} | Future Station` : defaultTitle;
  
  // Description padrão
  const defaultDescription = 'Future Station - Especialistas em soluções Fluig e TOTVS com mais de 10 anos de experiência. Automação de processos, consultoria técnica e desenvolvimento personalizado.';
  const metaDescription = description || defaultDescription;
  
  // Keywords padrão
  const defaultKeywords = ['fluig', 'totvs', 'consultoria', 'automação', 'processos', 'bpm', 'ecm', 'future station'];
  const allKeywords = [...defaultKeywords, ...keywords];
  
  // Schema.org data
  const schemaData = {
    '@context': 'https://schema.org',
    '@type': type === 'article' ? 'Article' : 'WebPage',
    headline: title,
    description: metaDescription,
    url: currentUrl,
    image: `${baseUrl}${image}`,
    ...(type === 'article' && article && {
      author: {
        '@type': 'Person',
        name: article.author
      },
      publisher: {
        '@type': 'Organization',
        name: 'Future Station',
        logo: {
          '@type': 'ImageObject',
          url: `${baseUrl}/logo.png`
        }
      },
      datePublished: article.publishedTime,
      dateModified: article.modifiedTime || article.publishedTime,
      articleSection: article.section,
      keywords: article.tags.join(', ')
    })
  };

  return (
    <Helmet>
      {/* Basic meta tags */}
      <title>{fullTitle}</title>
      <meta name="description" content={metaDescription} />
      <meta name="keywords" content={allKeywords.join(', ')} />
      <meta name="author" content={author || 'Future Station'} />
      
      {/* Canonical URL */}
      <link rel="canonical" href={currentUrl} />
      
      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={metaDescription} />
      <meta property="og:image" content={`${baseUrl}${image}`} />
      <meta property="og:url" content={currentUrl} />
      <meta property="og:site_name" content="Future Station" />
      <meta property="og:locale" content="pt_BR" />
      
      {/* Article specific Open Graph */}
      {type === 'article' && article && (
        <>
          <meta property="article:author" content={article.author} />
          <meta property="article:published_time" content={article.publishedTime} />
          {article.modifiedTime && (
            <meta property="article:modified_time" content={article.modifiedTime} />
          )}
          <meta property="article:section" content={article.section} />
          {article.tags.map((tag, index) => (
            <meta key={index} property="article:tag" content={tag} />
          ))}
        </>
      )}
      
      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={metaDescription} />
      <meta name="twitter:image" content={`${baseUrl}${image}`} />
      
      {/* Additional meta tags */}
      <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
      <meta name="googlebot" content="index, follow" />
      
      {/* Schema.org structured data */}
      <script type="application/ld+json">
        {JSON.stringify(schemaData)}
      </script>
      
      {/* Preconnects for performance */}
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
      
      {/* DNS prefetch */}
      <link rel="dns-prefetch" href="//api.whatsapp.com" />
      <link rel="dns-prefetch" href="//wa.me" />
    </Helmet>
  );
};

export default SEO;